import { useEffect, useState, useCallback } from "react";
import { deviceDetect } from "react-device-detect";

export const zoomToFontSizeMap = new Map([
  [100, 14],
  [70, 12],
  [60, 10],
  [50, 8],
  [40, 6],
  [30, 4],
  [20, 4],
  [10, 2],
]);

export const mobileZoomToFontSizeMap = new Map([
  [100, 14],
  [90, 12],
  [80, 10],
  [70, 8],
  [60, 8],
  [50, 6],
  [40, 5],
  [30, 4],
]);

const ZOOM_LEVELS = [30, 40, 50, 60, 70, 80, 90, 100, 110, 120];

const useTableBody = (
  zoomLevel: number,
  setZoomLevel: (zoomLevel: number) => void
) => {
  const [initialDistance, setInitialDistance] = useState(0);
  const [lastZoomUpdate, setLastZoomUpdate] = useState(Date.now());

  useEffect(() => {
    const newFontSize = deviceDetect(navigator.userAgent).isMobile
      ? mobileZoomToFontSizeMap.get(zoomLevel)
      : zoomToFontSizeMap.get(zoomLevel);

    if (newFontSize)
      document.documentElement.style.setProperty(
        "--dynamic-font-size",
        `${newFontSize || 12}px`
      );
  }, [zoomLevel]);

  const snapToNearestZoomLevel = useCallback((currentZoom: number) => {
    return ZOOM_LEVELS.reduce((prev, curr) =>
      Math.abs(curr - currentZoom) < Math.abs(prev - currentZoom) ? curr : prev
    );
  }, []);

  const handlePinchStart = useCallback((event: TouchEvent) => {
    const touch1 = event.touches[0];
    const touch2 = event.touches[1];

    if (!touch1 || !touch2) return;

    const distance = Math.sqrt(
      Math.pow(touch2.pageX - touch1.pageX, 2) +
        Math.pow(touch2.pageY - touch1.pageY, 2)
    );

    setInitialDistance(distance);
  }, []);

  const handlePinchMove = useCallback(
    (event: TouchEvent) => {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      if (!touch1 || !touch2) return;

      const distance = Math.sqrt(
        Math.pow(touch2.pageX - touch1.pageX, 2) +
          Math.pow(touch2.pageY - touch1.pageY, 2)
      );

      const scale = distance / initialDistance;
      const now = Date.now();

      if (now - lastZoomUpdate > 100) {
        let newZoomLevel;
        if (scale < 1) {
          newZoomLevel = Math.max(30, snapToNearestZoomLevel(zoomLevel - 10));
        } else {
          newZoomLevel = Math.min(120, snapToNearestZoomLevel(zoomLevel + 10));
        }

        if (newZoomLevel !== zoomLevel) {
          setZoomLevel(newZoomLevel);
          setLastZoomUpdate(now);
        }
      }
    },
    [
      initialDistance,
      zoomLevel,
      setZoomLevel,
      lastZoomUpdate,
      snapToNearestZoomLevel,
    ]
  );

  return { handlePinchStart, handlePinchMove };
};

export default useTableBody;
